import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import AddIcon from 'transfix-icons/build/AddIcon'
import { Button } from 'transfix-ui/components/Button'
import { Card } from 'transfix-ui/components/Card'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Table, TableBody } from 'transfix-ui/components/Table'
import { useGlobalToast } from 'transfix-ui/components/Toast/GlobalToast'
import { makeStyles, Box, Typography } from 'transfix-ui/core'
import Skeleton from 'transfix-ui/core/Skeleton'
import { brandColors } from 'transfix-ui/theme'
import { ArrayElement } from 'types'
import { IStore } from 'apps/carrier/utils/reducers'
import {
  useGetFMSLaneAlertDetails,
  LaneAlertResponse,
} from 'apps/marketplace/apiTypes/carrierLaneAlerts'
import { useModal } from 'utils/hooks'
import { AddLaneAlertModal } from './AddLaneAlertModal'
import { DeleteLaneAlertModal } from './DeleteLaneAlertModal'
import { LaneAlertTableEntryRow } from './LaneAlertTableEntryRow'
import { LaneAlertsTableHeaders } from './LaneAlertsTableHeaders'

interface IState {
  id?: string
  name?: string
}

const useLaneAlertStyles = makeStyles({
  empty: {
    padding: '14px',
    textAlign: 'center',
    backgroundColor: brandColors.coolGray1,
    color: brandColors.coolGray5,
  },
})

export function LaneAlerts() {
  const [deleteId, setDeleteId] = useState<number>(0)
  const [editLaneAlert, setEditLaneAlert] = useState<
    ArrayElement<LaneAlertResponse['laneAlerts']>
  >()
  const { openToast } = useGlobalToast()
  const { id: carrierId, name: carrierName } = useSelector<IStore, IState>(({ carrier }) => ({
    id: carrier.carrierDetails?.id,
    name: carrier.carrierDetails?.legalName,
  }))
  const { closeModal, isOpen: isModalOpen, openModal } = useModal()
  const classes = useLaneAlertStyles()
  const {
    data: laData,
    error: laError,
    loading: laLoading,
    refetch: refetchFmsLaneAlerts,
  } = useGetFMSLaneAlertDetails({
    carrierId: Number(carrierId),
  })
  const { laneAlerts } = laData || { laneAlerts: [] }
  const carrierLaneAlerts = laneAlerts.filter(
    laneAlert => laneAlert.sourceType === 'dispatcher_created'
  )
  const cmLaneAlerts = laneAlerts.filter(laneAlert => laneAlert.sourceType !== 'dispatcher_created')

  const deleteLaneAlert = () => {
    openToast({
      toastMessage: 'Successfully deleted lane alert.',
      toastType: 'default',
    })
    refetchFmsLaneAlerts()
  }

  const addLaneAlert = () => {
    openToast({
      toastMessage: 'Successfully added lane alert.',
      toastType: 'default',
    })
    refetchFmsLaneAlerts()
  }

  return (
    <Card dataTest='lane-alerts'>
      <Box mb={4}>
        <Typography variant='h3'>Lane Alerts</Typography>
        <Box mt={2}>Users will be notified when new loads are available to book.</Box>
      </Box>

      {!laLoading && laError ? (
        <InlineMessage message={laError.message} type={INLINE_MESSAGE_VARIANTS.ALERT} />
      ) : (
        <>
          <Box>
            <Box mb={2} mt={2}>
              <Typography variant='subtitle1'>Created by carrier users</Typography>
            </Box>
            {carrierLaneAlerts && !laLoading && !laError && carrierLaneAlerts.length > 0 && (
              <Table>
                <LaneAlertsTableHeaders />
                <TableBody>
                  {carrierLaneAlerts.map(laneAlert => (
                    <LaneAlertTableEntryRow key={laneAlert.id} laneAlert={laneAlert} />
                  ))}
                </TableBody>
              </Table>
            )}
            {laneAlerts && !laLoading && !laError && carrierLaneAlerts.length === 0 && (
              <Box className={classes.empty}>
                <Box>No carrier created lane alerts</Box>
                <Box>This carrier hasn&apos;t created any lane alerts yet</Box>
              </Box>
            )}
            {laLoading && !laError && (
              <Box display='flex' justifyContent='space-between'>
                <Skeleton variant='rectangular' width='24%' height='40px' />
                <Skeleton variant='rectangular' width='24%' height='40px' />
                <Skeleton variant='rectangular' width='24%' height='40px' />
                <Skeleton variant='rectangular' width='24%' height='40px' />
              </Box>
            )}
          </Box>

          <Box>
            <Box mt={2} className='row pv2'>
              <Box mb={0} mt={2} className='fl'>
                <Typography variant='subtitle1'>Created by CMs</Typography>
              </Box>
              <Box className='fr'>
                <Button
                  startIcon={<AddIcon />}
                  onClick={openModal('add')}
                  data-test='add-lane-alert-button'>
                  Add Lane Alert
                </Button>
              </Box>
            </Box>
            {cmLaneAlerts && !laLoading && !laError && cmLaneAlerts.length > 0 && (
              <Table>
                <LaneAlertsTableHeaders />
                <TableBody>
                  {cmLaneAlerts.map(laneAlert => (
                    <LaneAlertTableEntryRow
                      key={laneAlert.id}
                      laneAlert={laneAlert}
                      onOpenDeleteModal={id => {
                        if (id) {
                          setDeleteId(id)
                          openModal('delete')()
                        }
                      }}
                      onOpenEditModal={laneAlertElem => {
                        setEditLaneAlert(laneAlertElem)
                        openModal('add')()
                      }}
                    />
                  ))}
                </TableBody>
              </Table>
            )}
            {cmLaneAlerts && !laLoading && !laError && cmLaneAlerts.length === 0 && (
              <Box className={classes.empty}>
                <Box>No CM-created lane alerts</Box>
                <Box>No one at Transfix has set up lane alerts for this carrier</Box>
              </Box>
            )}
            {laLoading && !laError && (
              <Box display='flex' justifyContent='space-between'>
                <Skeleton variant='rectangular' width='24%' height='40px' />
                <Skeleton variant='rectangular' width='24%' height='40px' />
                <Skeleton variant='rectangular' width='24%' height='40px' />
                <Skeleton variant='rectangular' width='24%' height='40px' />
              </Box>
            )}
          </Box>
        </>
      )}
      {isModalOpen('delete') && deleteId && carrierId && (
        <DeleteLaneAlertModal
          carrierId={Number(carrierId)}
          deleteId={deleteId}
          onCloseModal={closeModal}
          onSuccess={deleteLaneAlert}
        />
      )}
      {isModalOpen('add') && carrierId && carrierName && (
        <AddLaneAlertModal
          closeModal={() => {
            setEditLaneAlert(undefined)
            closeModal()
          }}
          onSuccess={addLaneAlert}
          laneAlert={editLaneAlert}
          carrierId={Number(carrierId)}
          carrierName={carrierName}
        />
      )}
    </Card>
  )
}
